<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">{{ editing ? 'Editar pago' : 'Registrar pago' }}</h1>
    </div>

    <form class="custom-form" @submit.prevent="save" novalidate>
      <b-card tag="article" no-body style="max-width: 820px" class="custom-card">
        <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
          <h3>Datos del pago</h3>
        </b-card-header>
        <b-card-body class="custom-card-body p-0">
          <section class="form-section">
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Inicio del período *</label>
                  <input type="date" v-model="form.period_start" class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.period_start.$error }" />
                  <FormError v-if="$v.form.period_start.$error && !$v.form.period_start.required"
                    message="El inicio del período es requerido" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Fin del período *</label>
                  <input type="date" v-model="form.period_end" class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.period_end.$error }" />
                  <FormError v-if="$v.form.period_end.$error && !$v.form.period_end.required"
                    message="El fin del período es requerido" />
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group">
                  <label>Monto *</label>
                  <input type="number" step="0.01" min="0" v-model.number="form.amount"
                    class="custom-form-control" placeholder="0.00"
                    :class="{ 'custom-form-control-error': $v.form.amount.$error }" />
                  <FormError v-if="$v.form.amount.$error && !$v.form.amount.required"
                    message="El monto es requerido" />
                  <FormError v-if="$v.form.amount.$error && !$v.form.amount.minValue"
                    message="El monto no puede ser negativo" />
                </div>
              </div>
              <div class="col-md-2">
                <div class="form-group">
                  <label>Moneda</label>
                  <select v-model="form.currency" class="custom-form-control">
                    <option value="PEN">PEN</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>Fecha de pago</label>
                  <input type="date" v-model="form.payment_date" class="custom-form-control" />
                  <small class="text-muted">Vacío = aún no pagado</small>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>Método</label>
                  <select v-model="form.payment_method" class="custom-form-control">
                    <option :value="null">—</option>
                    <option v-for="m in methods" :key="m.key" :value="m.key">{{ m.label }}</option>
                  </select>
                </div>
              </div>

              <div class="col-md-12">
                <div class="form-group mb-0">
                  <label>Notas</label>
                  <textarea v-model="form.notes" class="custom-form-control" rows="2"
                    placeholder="Observaciones"></textarea>
                </div>
              </div>
            </div>
          </section>

          <section class="form-section">
            <h2 class="form-subtitle">Comprobante</h2>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Foto del comprobante</label>
                  <input type="file" accept="image/*" class="custom-form-control" @change="onFile($event)" />
                  <small v-if="existing.has_proof" class="d-block mt-1">
                    <button type="button" class="link-button" @click="viewProof">Ver comprobante actual</button>
                  </small>
                </div>
              </div>
            </div>
            <p class="text-muted small mb-0">Imagen hasta 5 MB.</p>
          </section>

          <section class="form-section">
            <div class="form-group-buttons text-right">
              <button type="button" class="button button-light" @click="goBack">
                {{ editing ? 'Volver' : 'Cancelar' }}
              </button>
              <button class="button button-primary" type="submit">Guardar</button>
            </div>
          </section>
        </b-card-body>
      </b-card>
    </form>

    <b-modal v-model="proofModal.show" size="lg" hide-footer title="Comprobante de pago">
      <div class="photo-frame text-center">
        <img v-if="proofModal.url" :src="proofModal.url" alt="Comprobante" class="photo-img" />
        <p v-else-if="proofModal.error" class="text-muted m-0 py-4">{{ proofModal.error }}</p>
        <p v-else class="text-muted m-0 py-4">Cargando imagen…</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert, FormError } from 'wize-admin'
import { required, minValue } from 'vuelidate/lib/validators'
import { FleetRentalService } from '@/core/services'

const METHODS = [
  { key: 'CASH', label: 'Efectivo' },
  { key: 'CARD', label: 'Tarjeta' },
  { key: 'TRANSFER', label: 'Transferencia' },
  { key: 'YAPE', label: 'Yape' },
  { key: 'PLIN', label: 'Plin' },
  { key: 'OTHER', label: 'Otro' }
]
const MAX_PHOTO_BYTES = 5 * 1024 * 1024

export default {
  name: 'RentalPaymentEditor',
  components: { FormError },
  data() {
    return {
      editing: false,
      methods: METHODS,
      vehicleId: null,
      form: {
        period_start: null,
        period_end: null,
        amount: null,
        currency: 'PEN',
        payment_date: null,
        payment_method: null,
        notes: null
      },
      photoFile: null,
      existing: { has_proof: false },
      proofModal: { show: false, url: null, error: null }
    }
  },
  validations: {
    form: {
      period_start: { required },
      period_end: { required },
      amount: { required, minValue: minValue(0) }
    }
  },
  methods: {
    onFile(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) { this.photoFile = null; return }
      if (!file.type.startsWith('image/')) {
        Alert.error('El archivo debe ser una imagen')
        event.target.value = ''
        return
      }
      if (file.size > MAX_PHOTO_BYTES) {
        Alert.error('La imagen supera el máximo de 5 MB')
        event.target.value = ''
        return
      }
      this.photoFile = file
    },
    buildPayload() {
      const f = this.form
      return {
        period_start: f.period_start,
        period_end: f.period_end,
        amount: Number(f.amount),
        currency: f.currency || 'PEN',
        payment_date: f.payment_date || null,
        payment_method: f.payment_method || null,
        notes: f.notes || null
      }
    },
    goBack() {
      if (this.vehicleId) {
        this.$router.push({ name: 'fleet-rental-payments', params: { vehicleId: this.vehicleId } })
      } else {
        this.$router.push({ name: 'fleet-rentals' })
      }
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({ scrollTo: document.querySelector('.form-error'), updateHistory: false, offset: -50 })
        })
        return
      }
      try {
        await this.$store.dispatch('app/loading', true)
        const payload = this.buildPayload()

        if (!this.editing) {
          const res = await FleetRentalService.createPayment(this.vehicleId, payload)
          const id = res.payload.id
          if (this.photoFile) await FleetRentalService.uploadProof(id, this.photoFile)
          Alert.success('Pago registrado correctamente')
          this.$router.push({ name: 'fleet-rental-payments', params: { vehicleId: this.vehicleId } })
        } else {
          const id = this.$route.params.id
          await FleetRentalService.updatePayment(id, payload)
          if (this.photoFile) await FleetRentalService.uploadProof(id, this.photoFile)
          this.photoFile = null
          Alert.success('Pago actualizado correctamente')
          await this.load(id)
        }
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo guardar el pago')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async load(id) {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await FleetRentalService.getPayment(id)
        const p = res.payload
        this.vehicleId = p.vehicle_id
        this.form = {
          period_start: p.period_start ? p.period_start.slice(0, 10) : null,
          period_end: p.period_end ? p.period_end.slice(0, 10) : null,
          amount: p.amount,
          currency: p.currency || 'PEN',
          payment_date: p.payment_date ? p.payment_date.slice(0, 10) : null,
          payment_method: p.payment_method || null,
          notes: p.notes
        }
        this.existing = { has_proof: p.has_proof }
        this.editing = true
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo cargar el pago')
        this.$router.push({ name: 'fleet-rentals' })
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async viewProof() {
      this.proofModal = { show: true, url: null, error: null }
      try {
        const res = await FleetRentalService.getProof(this.$route.params.id)
        this.proofModal.url = res.payload ? res.payload.url : null
        if (!this.proofModal.url) this.proofModal.error = 'No hay comprobante disponible.'
      } catch (e) {
        console.error(e)
        this.proofModal.error = e.errors ? e.errors.message : 'No se pudo cargar el comprobante.'
      }
    }
  },
  async created() {
    if (this.$route.params.id) {
      await this.load(this.$route.params.id)
    } else if (this.$route.params.vehicleId) {
      this.vehicleId = Number(this.$route.params.vehicleId)
    }
  }
}
</script>

<style lang="stylus" scoped>
.link-button
  background none
  border none
  padding 0
  color #007bff
  cursor pointer
  text-decoration underline
.photo-frame
  background #f4f5f7
  border 1px solid #e5e7eb
  border-radius 8px
  min-height 220px
  display flex
  align-items center
  justify-content center
  overflow hidden
.photo-img
  max-width 100%
  max-height 70vh
  object-fit contain
</style>
